import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import i18n from '@/i18n'
import { serverMessage } from '../utils/serverMessage'
import { askStudioAssistant, cancelStudioAssistant } from '../services/api'
import {
  assistantStorageKey,
  loadAssistantMessages,
  removeAssistantMessages,
  saveAssistantMessages,
} from '../utils/assistantStorage'
import { describeOperations, workingLabel } from '../utils/assistantChanges'

function messageId() {
  return globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`
}

function uuid() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16)
    return (character === 'x' ? random : ((random & 0x3) | 0x8)).toString(16)
  })
}

function requestError(error) {
  if (error?.code === 'ERR_CANCELED') return null
  const errors = error?.response?.data?.errors || {}
  const first = Object.values(errors).flat()[0]
  return first || serverMessage(error?.response?.data?.message) || error?.message
    || i18n.t('editorV2.assistant.errors.requestFailed')
}

const READING_MS = 1200

/**
 * The Smartico AI Assistant thread (Chat tab). Kept at page level so switching between the
 * Editor and Chat tabs never aborts a request.
 *
 * Replies show the model's intro, one bullet per operation the editor actually applied (in
 * the editor's labels) and the model's follow-up. Stop aborts the request and releases the
 * server's per-user lock, so the next message is accepted at once. A reply that arrives
 * after the user changed the ad waits for Apply / Discard; the latest applied reply offers
 * Undo.
 */
export function useEditorAssistant({ apiBase, editor, userId, enabled, models = [] }) {
  const draftIdRef = useRef(`draft-${messageId()}`)
  const requestRef = useRef(null)
  const phaseTimer = useRef(null)
  const [messages, setMessages] = useState([])
  const [status, setStatus] = useState('idle')
  const [working, setWorking] = useState(null)
  const [pending, setPending] = useState(null)
  const [undoableId, setUndoableId] = useState(null)
  const [model, setModel] = useState(models[0]?.id || null)
  const adKey = editor.adId || draftIdRef.current
  const storageKey = useMemo(() => assistantStorageKey(userId, adKey), [adKey, userId])
  const previousKeyRef = useRef(storageKey)
  const recordedJobs = useRef(new Set())

  useEffect(() => {
    if (!model && models[0]?.id) setModel(models[0].id)
  }, [model, models])

  useEffect(() => {
    const previousKey = previousKeyRef.current
    if (previousKey !== storageKey) {
      const previous = loadAssistantMessages(previousKey)
      if (previous.length && !loadAssistantMessages(storageKey).length) saveAssistantMessages(storageKey, previous)
      removeAssistantMessages(previousKey)
      previousKeyRef.current = storageKey
    }
    setMessages(loadAssistantMessages(storageKey))
  }, [storageKey])

  useEffect(() => {
    saveAssistantMessages(storageKey, messages)
  }, [messages, storageKey])

  useEffect(() => () => {
    clearTimeout(phaseTimer.current)
    requestRef.current?.controller.abort()
  }, [])

  // The Undo link belongs to the latest applied reply only while the editor still holds it.
  useEffect(() => {
    if (!editor.assistantUndoAvailable) setUndoableId(null)
  }, [editor.assistantUndoAvailable])

  const append = useCallback((entry) => {
    setMessages((current) => [...current, { id: messageId(), createdAt: new Date().toISOString(), ...entry }].slice(-20))
  }, [])

  const send = useCallback(async (content) => {
    const text = String(content || '').trim()
    if (!enabled || !text || status === 'working' || !editor.template) return
    const history = messages
      .filter((entry) => ['user', 'assistant'].includes(entry.role))
      .slice(-12)
      .map((entry) => ({
        role: entry.role,
        content: [entry.content, entry.followUp].filter(Boolean).join('\n'),
      }))
    append({ role: 'user', content: text })
    setStatus('working')
    setPending(null)
    setWorking(i18n.t('editorV2.assistant.working.reading'))
    clearTimeout(phaseTimer.current)
    phaseTimer.current = setTimeout(() => setWorking(workingLabel(text)), READING_MS)
    const controller = new AbortController()
    const clientRequestId = uuid()
    requestRef.current = { controller, clientRequestId }

    try {
      const response = await askStudioAssistant(apiBase, {
        adReference: editor.adId,
        templateRelease: editor.template.manifest.releaseKey,
        configuration: editor.configuration,
        previewAssets: { assetUrls: editor.assetUrls },
        selection: { selectedSlide: editor.selectedSlide, selectedItems: editor.previewSelections },
        message: text,
        history,
        includePreview: true,
        editorVersion: editor.editorVersion,
        model,
        clientRequestId,
      }, controller.signal)
      clearTimeout(phaseTimer.current)
      const operations = response.operations || []
      const changes = describeOperations(editor.template, editor.configuration, operations)
      const reply = {
        id: response.requestId || messageId(),
        role: 'assistant',
        content: response.message || '',
        followUp: response.followUp || '',
        changes,
        warnings: response.warnings || [],
      }
      if (operations.length) {
        setWorking(i18n.t('editorV2.assistant.working.applying', { count: operations.length }))
        const applied = await editor.applyAssistantOperations(operations, response.editorVersion)
        if (applied.stale) {
          setPending({ operations, messageId: reply.id })
        } else {
          setUndoableId(reply.id)
        }
      }
      append(reply)
    } catch (requestException) {
      clearTimeout(phaseTimer.current)
      const message = requestError(requestException)
      if (message) append({ role: 'error', content: message })
    } finally {
      requestRef.current = null
      setWorking(null)
      setStatus('idle')
    }
  }, [apiBase, append, editor, enabled, messages, model, status])

  const stop = useCallback(() => {
    const request = requestRef.current
    if (!request) return
    request.controller.abort()
    requestRef.current = null
    clearTimeout(phaseTimer.current)
    setWorking(null)
    setStatus('idle')
    cancelStudioAssistant(apiBase, request.clientRequestId).catch(() => {})
  }, [apiBase])

  const applyPending = useCallback(async () => {
    if (!pending) return
    try {
      await editor.applyAssistantOperations(pending.operations, null, true)
      setUndoableId(pending.messageId)
      setPending(null)
    } catch (applyError) {
      append({ role: 'error', content: applyError.message || i18n.t('editorV2.assistant.errors.applyFailed') })
    }
  }, [append, editor, pending])

  const undo = useCallback(() => {
    if (editor.undoAssistantOperations()) setUndoableId(null)
  }, [editor])

  // A finished generation run becomes part of the thread: the step list, then "Generate
  // Success" (or the failure) with the chat suggestions after it.
  const recordGeneration = useCallback((job) => {
    if (!job?.id || recordedJobs.current.has(job.id) || !['completed', 'failed'].includes(job.status)) return
    recordedJobs.current.add(job.id)
    append({
      role: 'generation',
      status: job.status,
      quality: job.quality || null,
      content: job.status === 'failed' ? (job.error || i18n.t('editorV2.assistant.generationFailed')) : '',
    })
  }, [append])

  const clear = useCallback(() => {
    setMessages([])
    setPending(null)
    setUndoableId(null)
    removeAssistantMessages(storageKey)
  }, [storageKey])

  const lastUser = messages.map((entry) => entry.role).lastIndexOf('user')
  const lastGeneration = messages.map((entry) => entry.role).lastIndexOf('generation')
  const showSuggestions = lastGeneration >= 0 && lastGeneration > lastUser
    && messages[lastGeneration].status === 'completed' && status !== 'working'

  return {
    enabled,
    messages,
    status,
    working,
    pending,
    undoableId,
    model,
    setModel,
    models,
    showSuggestions,
    send,
    stop,
    applyPending,
    discardPending: () => setPending(null),
    undo,
    recordGeneration,
    clear,
  }
}
